import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import CommonTable from 'components/CommonTable';
import { OneLineLabel } from 'components/CommonWidgets';
import { Modal, Row, Col, Spinner, Form as BSForm } from "react-bootstrap";
import styled from "styled-components";
import {
    CustomButton,
    CustomSelect,
    CustomInput,
    FormikFieldError,
} from "components/CustomForm";
import { MdClose } from "react-icons/md";
import { getLogs, getLogDetails } from 'network/apis/LogApi';
import { useQuery } from "react-query";
import { getPersianFormattedDate } from "helpers/dates";
import InfiniteScroll from 'react-infinite-scroll-component';
import { isBlank } from "helpers/strings";
import { openLink } from 'helpers/general';

const ModalHolder = styled(Modal)`
	font-family: 'YEKAN-BAKH-MEDIUM';
`;

const Header = styled.div`
	font-size: 18pt;
	width: 100%;
	text-align: center;
	margin-top: 0.5rem;
	margin-bottom: 1.5rem;
`;

const DetailBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid black;
    border-radius: 8px;
    min-height: 18rem;
    width: 100%;

    h5 {
        color: #BDBDBD;
    }

    .off {
        color: #BDBDBD;
        text-align: center;
    }
`;

const CloseButton = styled.div`
	background-color: #424250;
	width: 2rem;
	height: 2rem;
	border-radius: 8px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: white;
	position: absolute;
	top: 12px;
	right: 10px;
`;

const MoreLoadingLabel = styled.div`
    text-align: center;
    margin: 1rem 0 1rem 0;
    color: #BDBDBD;
`;

const NoLogsLabel = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #BDBDBD;
    min-height: 18rem;
    border: 1px solid black;
    border-radius: 8px;
    font-size: 14pt;
`;

const LogsModal = ({
    isOpen,
    onClose,
    data,
    //referrer could be 'admin' or 'province'
    referrer,
    ...rest
}) => {
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [selectedLog, setSelectedLog] = useState(-1);

    const { data: logsData, isLoading: loadingLogs } = useQuery(
        [`get-logs-${referrer}-${data?.user_id ?? 0}-${pagination.currentPage}`, pagination.currentPage],
        () => getLogs(data?.user_id, pagination.currentPage),
        {
            refetchOnWindowFocus: false,
        }
    );

    useEffect(() => {
        if (logsData?.data) {
            setPagination({ ...pagination, totalPages: logsData.data.num_pages });

            const currentLogs = [...logs];
            var allNewLogs = [...logs];

            //add new page items to last page (also check for duplicate items)
            logsData.data.items.forEach((item) => {
                if (!currentLogs.find((log) => log.pk === item.pk)) {
                    allNewLogs.push(item);
                }
            });

            setLogs(allNewLogs);
        }
    }, [logsData]);

    const { data: logDetails, isLoading: loadingDetail } = useQuery(
        [`get-log-${referrer}-${data?.user_id ?? 0}-${selectedLog}-${selectedLog}`],
        () => getLogDetails(selectedLog),
        {
            refetchOnWindowFocus: false,
            enabled: !isNaN(selectedLog) && selectedLog >= 0
        }
    );

    const { refetch: fetchExcel } = useQuery(
        [`export-logs-${referrer}-${data?.user_id ?? 0}`],
        () => getLogs(data?.user_id, null, true),
        {
            refetchOnWindowFocus: false,
            enabled: false,
			onSuccess: (data) => {
				const url = data?.data?.url;
				if (url) openLink(url);
			},
        }
    );

    const downloadExcel = () => {
        fetchExcel();
    };

    const detailIsAvailable = () => selectedLog >= 0 && logDetails?.data;

    const beforeLogIsAvailable = () => logDetails?.data?.before != null;

    const getBeforeLogs = (prop = 'key') => {
        const before = logDetails?.data?.after ?? {};
        return Object.keys(before).map((objectIndex) => `${before[objectIndex][prop]}`).reverse();
    };

    const getAfterLogs = (prop = 'key') => {
        const after = logDetails?.data?.after ?? {};
        return Object.keys(after).map((objectIndex) => `${after[objectIndex][prop]}`).reverse();
    };

    return (
        <ModalHolder show={isOpen} size={'xl'} centered animation={false} onHide={onClose} {...rest}>
            <Modal.Body className="px-5">
                <CloseButton onClick={onClose}>
                    <MdClose color="#fff" />
                </CloseButton>
                <Header>
                    <span>تاریخچه فعالیت ها</span>
                </Header>
                <Row className="px-2 d-flex align-items-center">
                    <Col md={2} className='d-flex justify-content-center'>
                        <CustomButton
                            fontSize={10}
                            onClick={downloadExcel}
                            color='#0278EE'
                        >
                            خروجی اکسل
                        </CustomButton>
                    </Col>
                    <Col md={6} className='d-flex justify-content-end'>
                        <OneLineLabel>{data?.union?.name}</OneLineLabel>
                        <OneLineLabel className='ms-2'>: نام تشکل</OneLineLabel>
                    </Col>
                    <Col md={4} className='d-flex justify-content-end'>
                        <OneLineLabel>{data?.full_name}</OneLineLabel>
                        <OneLineLabel className='ms-2'>: نام و نام خانوادگی کاربر‌</OneLineLabel>
                    </Col>
                </Row>
                <Row className='mt-4'>
                    <Col md={6}>
                        <DetailBox>
                            {loadingDetail ? (
                                <Spinner animation="border" role="status" size="sm" />
                            ) : (
                                <Fragment>
                                    {detailIsAvailable() ? (
                                        <div className="w-100 px-4">
                                            <Row className="d-flex justify-content-center px-2">
                                                <span className="text-end mb-2">: قبل</span>
                                                {beforeLogIsAvailable() ? (
                                                    <CommonTable cols={getBeforeLogs()}>
                                                        {getBeforeLogs('value').map((log) => (
                                                            <td>{isBlank(log) ? '-' : log}</td>
                                                        ))}
                                                    </CommonTable>
                                                ) : (
                                                    <span className="off">وجود ندارد</span>
                                                )}
                                            </Row>
                                            <Row className="d-flex justify-content-center mt-3 px-2">
                                                <span className="text-end mb-2">: بعد</span>
                                                <CommonTable cols={getAfterLogs()}>
                                                    {getAfterLogs('value').map((log) => (
                                                        <td>{isBlank(log) ? '-' : log}</td>
                                                    ))}
                                                </CommonTable>
                                            </Row>
                                        </div>
                                    ) : (
                                        <h5>برای نمایش جزئیات یک لاگ را انتخاب کنید</h5>
                                    )}
                                </Fragment>
                            )}
                        </DetailBox>
                    </Col>
                    <Col md={6}>
                        {logs.length > 0 ? (
                            <div
                                id="scrollableTable"
                                style={{
                                    width: '100%',
                                    height: '18rem',
                                    overflow: 'auto'
                                }}
                            >
                                <InfiniteScroll
                                    dataLength={logs?.length ?? 0}
                                    next={() => {
                                        setPagination({ ...pagination, currentPage: pagination.currentPage + 1 });
                                    }}
                                    hasMore={pagination.currentPage < pagination.totalPages}
                                    loader={<MoreLoadingLabel key={0}>... در حال بارگزاری</MoreLoadingLabel>}
                                    scrollableTarget='scrollableTable'
                                >
                                    <CommonTable
                                        cols={['زمان ثبت', 'جزئیات', 'عنوان', `ردیف`]}
                                        tablewidth="100%"
                                        maxHeight='18rem'
                                    >
                                        {logs.map((item, index) => (
                                            <tr key={`${item.pk}-{index}`} className="w-100">
                                                <td>{getPersianFormattedDate(item.created_at)}</td>
                                                <td>
                                                    <CustomButton
                                                        onClick={() => setSelectedLog(parseInt(item.pk))}
                                                        color='#0278EE'
                                                        fontSize={10}
                                                    >
                                                        نمایش
                                                    </CustomButton>
                                                </td>
                                                <td>{item.log_type}</td>
                                                <td>{index + 1}</td>
                                            </tr>
                                        ))}
                                    </CommonTable>
                                </InfiniteScroll>
                            </div>
                        ) : (
                            <NoLogsLabel>
                                {loadingLogs ? (
                                    <Spinner animation="border" role="status" size="sm" />
                                ) : (
                                    <span>هیچ رکوردی وجود ندارد</span>
                                )}
                            </NoLogsLabel>
                        )}
                    </Col>
                </Row>
            </Modal.Body>
        </ModalHolder>
    );
};

export default LogsModal;
